import { useCallback, useEffect, useState } from "react"
import { BASE_API_URL } from "../../Components/config"

const API = `${BASE_API_URL}/api/v1/pre-class-questions`

/**
 * Shared data layer for the pre-class Q&A. Students get their own questions,
 * instructors get everyone's — the backend decides based on the caller.
 */
export function usePreClassQuestions({ courseGuid, segmentGuid }) {
  const [questions, setQuestions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const token = localStorage.getItem("accessToken")
  const authHeaders = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }

  const refresh = useCallback(async () => {
    if (!courseGuid) return
    try {
      const params = segmentGuid ? `?course_segment_guid=${segmentGuid}` : ""
      const res = await fetch(`${API}/by-course-guid/${courseGuid}${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error(`load failed (${res.status})`)
      const data = await res.json()
      setQuestions(Array.isArray(data) ? data : [])
      setError(null)
    } catch (e) {
      setError("Could not load questions.")
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseGuid, segmentGuid])

  useEffect(() => {
    setLoading(true)
    refresh()
  }, [refresh])

  const ask = async (text) => {
    const res = await fetch(`${API}/`, {
      method: "POST",
      headers: authHeaders,
      body: JSON.stringify({
        course_guid: courseGuid,
        course_segment_guid: segmentGuid || null,
        question_text: text,
      }),
    })
    if (res.ok) {
      const q = await res.json()
      setQuestions((prev) => [q, ...prev])
    }
  }

  const answer = async (guid, answerText) => {
    const res = await fetch(`${API}/${guid}/answer`, {
      method: "PUT",
      headers: authHeaders,
      body: JSON.stringify({ answer_text: answerText }),
    })
    if (res.ok) {
      const updated = await res.json()
      setQuestions((prev) => prev.map((q) => (q.guid === guid ? updated : q)))
    }
  }

  const remove = async (guid) => {
    setQuestions((prev) => prev.filter((q) => q.guid !== guid))
    const res = await fetch(`${API}/${guid}`, {
      method: "DELETE",
      headers: authHeaders,
    })
    if (!res.ok) refresh()
  }

  return { questions, loading, error, refresh, ask, answer, remove }
}
